import type {
  LogCategory,
  CreateCategoryDto,
  UpdateCategoryDto,
} from "@repo/share-log-interfaces";
import type { StateStatus, StoreDeps } from "../types";
import type { ImmerSet } from "../helpers/create-store";
import { createStore } from "../helpers/create-store";
import { withFetchStatus } from "../helpers/with-fetch-status";
import { createReset } from "../helpers/create-reset";

interface CategoryState {
  categories: LogCategory[];
  status: StateStatus;
}

interface CategoryActions {
  fetchCategories: (workspaceId: number) => Promise<void>;
  createCategory: (
    workspaceId: number,
    dto: CreateCategoryDto,
  ) => Promise<LogCategory>;
  updateCategory: (
    workspaceId: number,
    categoryId: number,
    dto: UpdateCategoryDto,
  ) => Promise<LogCategory>;
  deleteCategory: (workspaceId: number, categoryId: number) => Promise<void>;
  reset: () => void;
}

export type CategoryStore = CategoryState & CategoryActions;

const initialState: CategoryState = {
  categories: [],
  status: "idle",
};

export function createCategoryStore(deps: StoreDeps) {
  return createStore<CategoryStore>("CategoryStore", (set) => ({
    ...initialState,

    fetchCategories: (workspaceId) =>
      withFetchStatus(
        (s) => set((state) => { state.status = s; }),
        deps.logger,
        async () => {
          const categories = await deps.api.category.list(workspaceId);
          set((state) => { state.categories = categories; });
        },
      ),

    createCategory: async (workspaceId, dto) => {
      const category = await deps.api.category.create(workspaceId, dto);
      set((state) => { state.categories.push(category); });
      return category;
    },

    updateCategory: async (workspaceId, categoryId, dto) => {
      const category = await deps.api.category.update(
        workspaceId,
        categoryId,
        dto,
      );
      set((state) => {
        const idx = state.categories.findIndex((c) => c.id === categoryId);
        if (idx !== -1) state.categories[idx] = category;
      });
      return category;
    },

    deleteCategory: async (workspaceId, categoryId) => {
      await deps.api.category.remove(workspaceId, categoryId);
      set((state) => {
        state.categories = state.categories.filter(
          (c) => c.id !== categoryId,
        );
      });
    },

    reset: createReset(set as ImmerSet<CategoryState>, initialState),
  }));
}
